import {
  ArrowBackIosOutlined,
  ArrowForwardIosOutlined,
  CheckBox,
  CheckBoxOutlineBlank
} from '@mui/icons-material';
import { Box, Checkbox, FormControlLabel, Grid, IconButton, Typography } from '@mui/material';
import { COSCellProps, COSTable, COSTextField } from '../../helpers/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Axios from 'axios';
import { TechnologyResponse } from '../../services/api';
import { parseQuery } from '../../helpers/url';
import { useNavigate } from 'react-router-dom';
import useTechnologies from '../../helpers/hooks/useTechnologies';
import withErrorHandler from '../../helpers/hoc/withErrorHandler/withErrorHandler';

const apiFetchSize = 25;

const TechnologiesComponent = () => {
  const { selectedPage } = parseQuery(location.search);
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const { technologies, loading, setParameters } = useTechnologies(
    undefined,
    false,
    [],
    apiFetchSize,
    0
  );
  const [page, setPage] = useState<number>(selectedPage ? +selectedPage : 0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setParameters([search, showInactive, [], apiFetchSize, page * apiFetchSize]);
  }, [showInactive]);

  const onEnter = useCallback(() => {
    setPage(0);
    setParameters([search, showInactive, [], apiFetchSize, 0]);
    navigate('/technologies');
  }, [search, showInactive]);

  useEffect(() => {
    setParameters([search, showInactive, [], apiFetchSize, page * apiFetchSize]);
    navigate(`/technologies?selectedPage=${page}`);
  }, [page]);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [technologies]);

  return (
    <Box sx={{ margin: 2 }}>
      <Typography align="center" variant="h5" component="h6">
        Technologies
      </Typography>
      <Box sx={{ margin: 2 }}>
        <Grid container spacing={1}>
          <Grid item lg={9}>
            <COSTextField
              label="Search"
              value={search}
              onChange={(value: string) => setSearch(value)}
              onEnter={onEnter}
            />
          </Grid>
          <Grid item lg={3} sx={{ marginTop: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    icon={<CheckBoxOutlineBlank />}
                    checkedIcon={<CheckBox />}
                    checked={showInactive}
                    onChange={() => setShowInactive(!showInactive)}
                    color="primary"
                  />
                }
                label="Show Inactive"
              />
            </Box>
          </Grid>
          <Grid item lg={12}>
            <COSTable<TechnologyResponse>
              isLoading={loading}
              data={technologies}
              muiTableContainerProps={{
                ref: tableContainerRef,
                sx: { maxHeight: '60vh', minHeight: '300px' }
              }}
              columns={[
                {
                  accessorKey: 'id',
                  header: '#',
                  size: 50,
                  Cell: ({ row }: COSCellProps<TechnologyResponse>) =>
                    row.index + page * apiFetchSize + 1
                },
                {
                  accessorKey: 'name',
                  header: 'Name',
                  enableColumnOrdering: true,
                  enableSorting: true,
                  size: 950
                }
              ]}
              renderBottomToolbarCustomActions={() => (
                <Box sx={{ display: 'flex', margin: 'auto', justifyContent: 'space-around' }}>
                  <IconButton
                    onClick={() => setPage(page > 0 ? page - 1 : 0)}
                    disabled={page === 0}
                  >
                    <ArrowBackIosOutlined />
                  </IconButton>
                  <Box sx={{ marginLeft: 2, marginRight: 2, paddingTop: 0.5, color: '#717171' }}>
                    <Typography align="center" variant="h4" component="h5">
                      {page + 1}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => setPage(page + 1)}
                    disabled={technologies.length < apiFetchSize}
                  >
                    <ArrowForwardIosOutlined />
                  </IconButton>
                </Box>
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default withErrorHandler(TechnologiesComponent, Axios);
